import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ChevronRight,
  Download,
  FileImage,
  FileText,
  FileType,
  Folder,
  FolderPlus,
  FolderUp,
  Loader2,
  MoreHorizontal,
  Pencil,
  PenTool,
  Presentation,
  Search,
  Trash2,
  Type,
  Upload,
  FolderInput,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Textarea } from '@/components/ui/Textarea';
import { useAuth } from '@/app/AuthProvider';
import {
  openAdminFile,
  useAdminFiles,
  useAdminFolders,
  useCreateAdminFolder,
  useDeleteAdminFile,
  useDeleteAdminFolder,
  useMoveAdminFolder,
  useRenameAdminFolder,
  useSeedAdminFolders,
  useUpdateAdminFile,
  useUploadAdminFiles,
  type AdminFileWithUploader,
  type AdminFolder,
  type AdminUploadResult,
} from '@/features/admin/hooks';
import {
  ADMIN_FILE_KIND_LABELS,
  MAX_ADMIN_FILE_BYTES,
  adminFileKind,
  adminLibraryAccept,
  adminLibraryAllowedSummary,
  formatBytes,
  friendlyAdminError,
  preserveAllowedExtension,
  type AdminFileKind,
} from '@/features/admin/model';
import {
  FOLDER_INPUT_PROPS,
  pickedFromDataTransfer,
  pickedFromFileList,
  type PickedFile,
} from '@/lib/fileTree';
import { formatRelative } from '@/lib/formatters';
import { canWrite } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import type { AdminLibrary } from '@/types/database';

const ROOT = '__root__';

type DriveDialog =
  | { kind: 'new-folder' }
  | { kind: 'rename-folder'; folder: AdminFolder }
  | { kind: 'details'; file: AdminFileWithUploader }
  | { kind: 'move-folder'; folder: AdminFolder }
  | { kind: 'move-file'; file: AdminFileWithUploader }
  | { kind: 'delete-folder'; folder: AdminFolder }
  | { kind: 'delete-file'; file: AdminFileWithUploader };

interface AdminDriveProps {
  library: AdminLibrary;
  title: string;
  starterFolders: readonly string[];
  emptyTitle: string;
}

export function AdminDrive({ library, title, starterFolders, emptyTitle }: AdminDriveProps) {
  const { role } = useAuth();
  const canEdit = canWrite(role, 'company_admin');
  const foldersQuery = useAdminFolders(library);
  const filesQuery = useAdminFiles(library);
  const upload = useUploadAdminFiles();
  const seed = useSeedAdminFolders();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState<DriveDialog | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const folders = useMemo(() => foldersQuery.data ?? [], [foldersQuery.data]);
  const files = useMemo(() => filesQuery.data ?? [], [filesQuery.data]);
  const requestedFolder = params.get('folder');
  const folderId =
    requestedFolder && folders.some((folder) => folder.id === requestedFolder)
      ? requestedFolder
      : null;

  useEffect(() => {
    if (foldersQuery.isLoading || !requestedFolder) return;
    if (!folders.some((folder) => folder.id === requestedFolder)) {
      setParams({}, { replace: true });
    }
  }, [folders, foldersQuery.isLoading, requestedFolder, setParams]);

  const trail = useMemo(() => folderTrail(folders, folderId), [folders, folderId]);
  const searching = query.trim().length > 0;
  const needle = query.trim().toLowerCase();

  const visibleFolders = useMemo(() => {
    const list = searching
      ? folders.filter((folder) => folder.name.toLowerCase().includes(needle))
      : folders.filter((folder) => folder.parent_id === folderId);
    return [...list].sort(byName);
  }, [folders, folderId, needle, searching]);

  const visibleFiles = useMemo(() => {
    const list = searching
      ? files.filter(
          (file) =>
            file.name.toLowerCase().includes(needle) ||
            (file.description ?? '').toLowerCase().includes(needle),
        )
      : files.filter((file) => file.folder_id === folderId);
    return [...list].sort(byName);
  }, [files, folderId, needle, searching]);

  const isEmptyLibrary = folders.length === 0 && files.length === 0;
  const isLoading = foldersQuery.isLoading || filesQuery.isLoading;
  const isError = foldersQuery.isError || filesQuery.isError;

  function openFolder(id: string | null) {
    setQuery('');
    if (id) setParams({ folder: id });
    else setParams({});
  }

  async function handleFiles(picked: PickedFile[]) {
    if (!picked.length) {
      toast.info('Nothing to upload in that selection.');
      return;
    }
    setProgress({ done: 0, total: picked.length });
    try {
      const result = await upload.mutateAsync({
        library,
        folderId,
        files: picked,
        onProgress: (done, total) => setProgress({ done, total }),
      });
      const summary = uploadSummary(result);
      if (result.uploaded && result.failures.length) {
        const more = result.failures.length > 1 ? ` (+${result.failures.length - 1} more)` : '';
        toast.warning(`${summary} Skipped ${result.failures[0]}${more}`);
      } else if (result.failures.length) {
        toast.error(result.failures[0] ?? 'Upload failed.');
      } else if (result.uploaded) {
        toast.success(summary);
      }
    } catch (err) {
      toast.error(friendlyAdminError(err, 'Upload failed.'));
    } finally {
      setProgress(null);
    }
  }

  async function handleDrop(transfer: DataTransfer) {
    try {
      const picked = await pickedFromDataTransfer(transfer);
      await handleFiles(picked);
    } catch (err) {
      toast.error(friendlyAdminError(err, 'Could not read what was dropped.'));
    }
  }

  async function handleOpenFile(file: AdminFileWithUploader) {
    try {
      await openAdminFile(file.storage_path, file.name);
    } catch (err) {
      toast.error(friendlyAdminError(err, 'Could not open that file.'));
    }
  }

  const accept = adminLibraryAccept(library);
  const allowedSummary = adminLibraryAllowedSummary(library);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Admin"
        title={title}
        actions={
          canEdit ? (
            <>
              <Button
                variant="outline"
                onClick={() => setDialog({ kind: 'new-folder' })}
                disabled={upload.isPending}
              >
                <FolderPlus className="h-4 w-4" />
                New folder
              </Button>
              <Button
                variant="outline"
                onClick={() => folderInputRef.current?.click()}
                disabled={upload.isPending}
              >
                <FolderUp className="h-4 w-4" />
                Upload folder
              </Button>
              <Button onClick={() => fileInputRef.current?.click()} disabled={upload.isPending}>
                {upload.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                {progress ? `Uploading ${progress.done}/${progress.total}` : 'Upload'}
              </Button>
            </>
          ) : undefined
        }
      />

      <input
        ref={fileInputRef}
        type="file"
        className="sr-only"
        accept={accept}
        multiple
        onChange={(event) => {
          if (event.target.files) void handleFiles(pickedFromFileList(event.target.files));
          event.target.value = '';
        }}
      />
      {/* No `accept` here: it would hide the folder's other files in the
          picker on some browsers; the allowlist is enforced on upload. */}
      <input
        ref={folderInputRef}
        type="file"
        className="sr-only"
        multiple
        {...FOLDER_INPUT_PROPS}
        onChange={(event) => {
          if (event.target.files) void handleFiles(pickedFromFileList(event.target.files));
          event.target.value = '';
        }}
      />

      <section
        className={cn(
          'rounded-lg border border-border bg-card shadow-[0_1px_2px_0_hsl(124_26%_14%/0.04)]',
          dragOver && canEdit && 'border-info bg-info-soft/20',
        )}
        onDragOver={(event) => {
          if (!canEdit) return;
          event.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => {
          if (!canEdit) return;
          event.preventDefault();
          setDragOver(false);
          void handleDrop(event.dataTransfer);
        }}
      >
        <div className="flex flex-col gap-3 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <nav aria-label="Folder" className="flex min-w-0 flex-wrap items-center gap-1 text-sm">
            {searching ? (
              <span className="text-foreground">
                Results for <span className="font-medium">“{query.trim()}”</span>
              </span>
            ) : (
              <>
                <Crumb onClick={folderId ? () => openFolder(null) : undefined}>{title}</Crumb>
                {trail.map((folder, index) => {
                  const last = index === trail.length - 1;
                  return (
                    <span key={folder.id} className="flex min-w-0 items-center gap-1">
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <Crumb onClick={last ? undefined : () => openFolder(folder.id)}>
                        {folder.name}
                      </Crumb>
                    </span>
                  );
                })}
              </>
            )}
          </nav>
          <div className="relative w-full sm:w-64">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search this library"
              className="pl-9"
              aria-label={`Search ${title}`}
            />
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : isError ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">
            Couldn’t load this library. Refresh and try again.
          </p>
        ) : isEmptyLibrary ? (
          <div className="p-4">
            <EmptyState
              icon={Folder}
              title={emptyTitle}
              action={
                canEdit ? (
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() =>
                        seed.mutate(
                          { library, names: starterFolders },
                          {
                            onSuccess: () => toast.success('Starter folders added.'),
                            onError: (err) =>
                              toast.error(friendlyAdminError(err, 'Could not add folders.')),
                          },
                        )
                      }
                      disabled={seed.isPending}
                    >
                      {seed.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <FolderPlus className="h-4 w-4" />
                      )}
                      Add starter folders
                    </Button>
                    <Button variant="outline" onClick={() => folderInputRef.current?.click()}>
                      <FolderUp className="h-4 w-4" />
                      Upload folder
                    </Button>
                    <Button onClick={() => fileInputRef.current?.click()}>
                      <Upload className="h-4 w-4" />
                      Upload
                    </Button>
                  </div>
                ) : undefined
              }
            />
          </div>
        ) : visibleFolders.length === 0 && visibleFiles.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">
            {searching ? 'Nothing matches that search.' : 'This folder is empty.'}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {visibleFolders.map((folder) => (
              <FolderRow
                key={folder.id}
                folder={folder}
                folders={folders}
                files={files}
                showPath={searching}
                canEdit={canEdit}
                onOpen={() => openFolder(folder.id)}
                onDialog={setDialog}
              />
            ))}
            {visibleFiles.map((file) => (
              <FileRow
                key={file.id}
                file={file}
                folders={folders}
                showPath={searching}
                canEdit={canEdit}
                onOpen={() => void handleOpenFile(file)}
                onDialog={setDialog}
              />
            ))}
          </ul>
        )}

        {canEdit && (
          <p className="border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
            Drop files or whole folders here — subfolders are recreated. Accepts {allowedSummary} up
            to {Math.round(MAX_ADMIN_FILE_BYTES / (1024 * 1024))} MB each.
          </p>
        )}
      </section>

      <DriveDialogs
        library={library}
        dialog={dialog}
        folders={folders}
        files={files}
        currentFolderId={folderId}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setQuery('');
          setDialog(null);
        }}
      />
    </div>
  );
}

function Crumb({ children, onClick }: { children: string; onClick?: () => void }) {
  if (!onClick) {
    return <span className="truncate font-medium text-foreground">{children}</span>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="truncate text-muted-foreground hover:text-foreground"
    >
      {children}
    </button>
  );
}

function FolderRow({
  folder,
  folders,
  files,
  showPath,
  canEdit,
  onOpen,
  onDialog,
}: {
  folder: AdminFolder;
  folders: AdminFolder[];
  files: AdminFileWithUploader[];
  showPath: boolean;
  canEdit: boolean;
  onOpen: () => void;
  onDialog: (dialog: DriveDialog) => void;
}) {
  const childFolders = folders.filter((item) => item.parent_id === folder.id).length;
  const childFiles = files.filter((item) => item.folder_id === folder.id).length;
  const count = childFolders + childFiles;
  return (
    <li className="flex items-center gap-2 px-2 py-1.5 hover:bg-secondary/40 sm:px-3">
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-md px-2 py-2 text-left"
      >
        <Folder className="h-5 w-5 shrink-0 text-info" />
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-foreground">{folder.name}</span>
          {showPath && (
            <span className="block truncate text-xs text-muted-foreground">
              {folderPathLabel(folders, folder.parent_id)}
            </span>
          )}
        </span>
      </button>
      <span className="hidden shrink-0 text-xs text-muted-foreground sm:block">
        {count === 1 ? '1 item' : `${count} items`}
      </span>
      {canEdit && (
        <RowMenu>
          <DropdownMenuItem className="gap-2" onClick={onOpen}>
            <Folder className="h-4 w-4" />
            Open
          </DropdownMenuItem>
          <DropdownMenuItem
            className="gap-2"
            onClick={() => onDialog({ kind: 'rename-folder', folder })}
          >
            <Pencil className="h-4 w-4" />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem
            className="gap-2"
            onClick={() => onDialog({ kind: 'move-folder', folder })}
          >
            <FolderInput className="h-4 w-4" />
            Move
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="gap-2 text-destructive focus:text-destructive"
            onClick={() => onDialog({ kind: 'delete-folder', folder })}
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </RowMenu>
      )}
    </li>
  );
}

function FileRow({
  file,
  folders,
  showPath,
  canEdit,
  onOpen,
  onDialog,
}: {
  file: AdminFileWithUploader;
  folders: AdminFolder[];
  showPath: boolean;
  canEdit: boolean;
  onOpen: () => void;
  onDialog: (dialog: DriveDialog) => void;
}) {
  const kind = adminFileKind(file.name);
  const who = file.uploader?.full_name || file.uploader?.email;
  return (
    <li className="flex items-center gap-2 px-2 py-1.5 hover:bg-secondary/40 sm:px-3">
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-md px-2 py-2 text-left"
      >
        <FileGlyph kind={kind} />
        <span className="min-w-0">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm font-medium text-foreground">{file.name}</span>
            <Badge variant="secondary" className="hidden sm:inline-flex">
              {ADMIN_FILE_KIND_LABELS[kind]}
            </Badge>
          </span>
          <span className="block truncate text-xs text-muted-foreground">
            {showPath
              ? folderPathLabel(folders, file.folder_id)
              : file.description ||
                [formatBytes(file.size_bytes), who, formatRelative(file.updated_at)]
                  .filter(Boolean)
                  .join(' · ')}
          </span>
        </span>
      </button>
      <span className="hidden shrink-0 text-xs text-muted-foreground md:block">
        {formatBytes(file.size_bytes)}
      </span>
      <RowMenu>
        <DropdownMenuItem className="gap-2" onClick={onOpen}>
          <Download className="h-4 w-4" />
          Download
        </DropdownMenuItem>
        {canEdit && (
          <>
            <DropdownMenuItem className="gap-2" onClick={() => onDialog({ kind: 'details', file })}>
              <Pencil className="h-4 w-4" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem
              className="gap-2"
              onClick={() => onDialog({ kind: 'move-file', file })}
            >
              <FolderInput className="h-4 w-4" />
              Move
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="gap-2 text-destructive focus:text-destructive"
              onClick={() => onDialog({ kind: 'delete-file', file })}
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </>
        )}
      </RowMenu>
    </li>
  );
}

function RowMenu({ children }: { children: ReactNode }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" aria-label="More actions">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FileGlyph({ kind }: { kind: AdminFileKind }) {
  if (kind === 'word') return <FileType className="h-5 w-5 shrink-0 text-info" />;
  if (kind === 'image') return <FileImage className="h-5 w-5 shrink-0 text-bright-moss-dark" />;
  if (kind === 'pdf') return <FileText className="h-5 w-5 shrink-0 text-fresh-salmon-dark" />;
  if (kind === 'font') return <Type className="h-5 w-5 shrink-0 text-foreground" />;
  if (kind === 'design') return <PenTool className="h-5 w-5 shrink-0 text-fresh-salmon-dark" />;
  if (kind === 'slides') return <Presentation className="h-5 w-5 shrink-0 text-info" />;
  return <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />;
}

function DriveDialogs({
  library,
  dialog,
  folders,
  files,
  currentFolderId,
  onClose,
  onSaved,
}: {
  library: AdminLibrary;
  dialog: DriveDialog | null;
  folders: AdminFolder[];
  files: AdminFileWithUploader[];
  currentFolderId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const createFolder = useCreateAdminFolder();
  const renameFolder = useRenameAdminFolder();
  const moveFolder = useMoveAdminFolder();
  const deleteFolder = useDeleteAdminFolder();
  const updateFile = useUpdateAdminFile();
  const deleteFile = useDeleteAdminFile();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [destination, setDestination] = useState<string>(ROOT);

  useEffect(() => {
    if (!dialog) return;
    if (dialog.kind === 'rename-folder') setName(dialog.folder.name);
    else if (dialog.kind === 'details') {
      setName(dialog.file.name);
      setDescription(dialog.file.description ?? '');
    } else if (dialog.kind === 'new-folder') setName('');
  }, [dialog]);

  const pending =
    createFolder.isPending ||
    renameFolder.isPending ||
    moveFolder.isPending ||
    deleteFolder.isPending ||
    updateFile.isPending ||
    deleteFile.isPending;

  const moveContext = useMemo(() => {
    if (!dialog || (dialog.kind !== 'move-folder' && dialog.kind !== 'move-file')) return null;
    const blocked = new Set<string>();
    const currentParent =
      dialog.kind === 'move-folder' ? dialog.folder.parent_id : dialog.file.folder_id;
    if (dialog.kind === 'move-folder') {
      blocked.add(dialog.folder.id);
      for (const id of descendantFolderIds(folders, dialog.folder.id)) blocked.add(id);
    }
    return {
      currentParent,
      options: moveOptions(folders, currentParent, blocked),
    };
  }, [dialog, folders]);

  useEffect(() => {
    if (!moveContext || moveContext.options.length === 0) return;
    setDestination(moveContext.options[0]?.value ?? ROOT);
  }, [moveContext]);

  async function run(action: () => Promise<unknown>, success: string) {
    try {
      await action();
      toast.success(success);
      onSaved();
    } catch (err) {
      toast.error(friendlyAdminError(err, 'Something went wrong.'));
    }
  }

  return (
    <Dialog open={!!dialog} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        {dialog?.kind === 'new-folder' && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void run(
                () =>
                  createFolder.mutateAsync({
                    library,
                    parentId: currentFolderId,
                    name,
                  }),
                'Folder created.',
              );
            }}
          >
            <DialogHeader>
              <DialogTitle>New folder</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="admin-folder-name">Name</Label>
              <Input
                id="admin-folder-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoFocus
                className="mt-1.5"
                maxLength={80}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={pending || !name.trim()}>
                Create
              </Button>
            </DialogFooter>
          </form>
        )}

        {dialog?.kind === 'rename-folder' && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const folder = dialog.folder;
              void run(
                () => renameFolder.mutateAsync({ library, id: folder.id, name }),
                'Folder renamed.',
              );
            }}
          >
            <DialogHeader>
              <DialogTitle>Rename folder</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="admin-folder-rename">Name</Label>
              <Input
                id="admin-folder-rename"
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoFocus
                className="mt-1.5"
                maxLength={80}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={pending || !name.trim()}>
                Save
              </Button>
            </DialogFooter>
          </form>
        )}

        {dialog?.kind === 'details' && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const file = dialog.file;
              void run(
                () =>
                  updateFile.mutateAsync({
                    library,
                    id: file.id,
                    name: preserveAllowedExtension(library, name, file.name),
                    description,
                  }),
                'File updated.',
              );
            }}
          >
            <DialogHeader>
              <DialogTitle>Rename file</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-4">
              <div>
                <Label htmlFor="admin-file-name">Name</Label>
                <Input
                  id="admin-file-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  autoFocus
                  className="mt-1.5"
                  maxLength={180}
                />
              </div>
              <div>
                <Label htmlFor="admin-file-description">Note</Label>
                <Textarea
                  id="admin-file-description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  className="mt-1.5"
                  maxLength={500}
                  placeholder="Who this form is for, or when to use it"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={pending || !name.trim()}>
                Save
              </Button>
            </DialogFooter>
          </form>
        )}

        {(dialog?.kind === 'move-folder' || dialog?.kind === 'move-file') && moveContext && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const parentId = destination === ROOT ? null : destination;
              if (dialog.kind === 'move-folder') {
                const folder = dialog.folder;
                void run(
                  () => moveFolder.mutateAsync({ library, id: folder.id, parentId }),
                  'Folder moved.',
                );
              } else {
                const file = dialog.file;
                void run(
                  () => updateFile.mutateAsync({ library, id: file.id, folderId: parentId }),
                  'File moved.',
                );
              }
            }}
          >
            <DialogHeader>
              <DialogTitle>Move</DialogTitle>
              <DialogDescription>
                {dialog.kind === 'move-folder' ? dialog.folder.name : dialog.file.name}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {moveContext.options.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nowhere else to move this yet.</p>
              ) : (
                <>
                  <Label>Destination</Label>
                  <Select value={destination} onValueChange={setDestination}>
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {moveContext.options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={pending || moveContext.options.length === 0}>
                Move
              </Button>
            </DialogFooter>
          </form>
        )}

        {dialog?.kind === 'delete-folder' && (
          <>
            <DialogHeader>
              <DialogTitle>Delete {dialog.folder.name}?</DialogTitle>
              <DialogDescription>
                {deleteFolderSummary(dialog.folder, folders, files)} This can’t be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={pending}
                onClick={() => {
                  const folder = dialog.folder;
                  const ids = descendantFolderIds(folders, folder.id);
                  ids.add(folder.id);
                  const storagePaths = files
                    .filter((file) => file.folder_id != null && ids.has(file.folder_id))
                    .map((file) => file.storage_path);
                  void run(
                    () => deleteFolder.mutateAsync({ library, id: folder.id, storagePaths }),
                    'Folder deleted.',
                  );
                }}
              >
                Delete
              </Button>
            </DialogFooter>
          </>
        )}

        {dialog?.kind === 'delete-file' && (
          <>
            <DialogHeader>
              <DialogTitle>Delete {dialog.file.name}?</DialogTitle>
              <DialogDescription>
                The file is removed from the shared library. Copies people already downloaded stay
                with them.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={pending}
                onClick={() => {
                  const file = dialog.file;
                  void run(
                    () =>
                      deleteFile.mutateAsync({
                        library,
                        id: file.id,
                        storagePath: file.storage_path,
                      }),
                    'File deleted.',
                  );
                }}
              >
                Delete
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function uploadSummary(result: AdminUploadResult): string {
  const files = result.uploaded === 1 ? '1 file' : `${result.uploaded} files`;
  if (!result.foldersCreated) return `Uploaded ${files}.`;
  const folders =
    result.foldersCreated === 1 ? '1 new folder' : `${result.foldersCreated} new folders`;
  return `Uploaded ${files} into ${folders}.`;
}

function byName<T extends { name: string }>(a: T, b: T) {
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}

function folderTrail(folders: AdminFolder[], folderId: string | null): AdminFolder[] {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const trail: AdminFolder[] = [];
  const seen = new Set<string>();
  let current = folderId;
  while (current) {
    if (seen.has(current)) break;
    seen.add(current);
    const folder = byId.get(current);
    if (!folder) break;
    trail.push(folder);
    current = folder.parent_id;
  }
  return trail.reverse();
}

function folderPathLabel(folders: AdminFolder[], folderId: string | null): string {
  const trail = folderTrail(folders, folderId);
  return trail.length ? trail.map((folder) => folder.name).join(' / ') : 'Library root';
}

function descendantFolderIds(folders: AdminFolder[], rootId: string): Set<string> {
  const children = new Map<string, string[]>();
  for (const folder of folders) {
    if (!folder.parent_id) continue;
    const list = children.get(folder.parent_id) ?? [];
    list.push(folder.id);
    children.set(folder.parent_id, list);
  }
  const out = new Set<string>();
  const stack = [...(children.get(rootId) ?? [])];
  while (stack.length) {
    const id = stack.pop();
    if (!id || out.has(id)) continue;
    out.add(id);
    stack.push(...(children.get(id) ?? []));
  }
  return out;
}

function moveOptions(
  folders: AdminFolder[],
  currentParent: string | null,
  blocked: Set<string>,
): Array<{ value: string; label: string }> {
  const options: Array<{ value: string; label: string }> = [];
  if (currentParent !== null) {
    options.push({ value: ROOT, label: 'Library root' });
  }
  const walk = (parentId: string | null, depth: number) => {
    const children = folders.filter((folder) => folder.parent_id === parentId).sort(byName);
    for (const child of children) {
      if (blocked.has(child.id)) continue;
      if (child.id !== currentParent) {
        const prefix = depth > 0 ? `${'· '.repeat(depth)}` : '';
        options.push({ value: child.id, label: `${prefix}${child.name}` });
      }
      walk(child.id, child.id === currentParent ? depth : depth + 1);
    }
  };
  walk(null, 0);
  return options;
}

function deleteFolderSummary(
  folder: AdminFolder,
  folders: AdminFolder[],
  files: AdminFileWithUploader[],
): string {
  const ids = descendantFolderIds(folders, folder.id);
  const nested = ids.size;
  ids.add(folder.id);
  const fileCount = files.filter(
    (file) => file.folder_id != null && ids.has(file.folder_id),
  ).length;
  if (nested === 0 && fileCount === 0) return 'This folder is empty.';
  const folderBit = nested === 1 ? '1 folder' : `${nested} folders`;
  const fileBit = fileCount === 1 ? '1 file' : `${fileCount} files`;
  if (nested && fileCount) return `This also deletes ${folderBit} and ${fileBit} inside it.`;
  if (fileCount) return `This also deletes ${fileBit} inside it.`;
  return `This also deletes ${folderBit} inside it.`;
}
